/**
 * 3-digit ZIP Code prefix ranges to US state, used to fill in the state on a
 * registration address screen once a full ZIP is typed. Carried over from the
 * legacy web registration unchanged, so both surfaces fill the same state.
 *
 * Source: USPS 3-digit prefix assignments by sectional center facility, as
 * listed on Wikipedia's "Sectional center facility" page (which cites USPS
 * FAST labeling lists L002/L005), retrieved 2026-10-05. Prefixes served by a
 * facility in another state are assigned to the state the prefix belongs to
 * (e.g. 679 is Kansas although Amarillo, TX serves it).
 *
 * Only the 50 states and DC. Territories, military (APO/FPO) and unassigned
 * or special-use prefixes (e.g. 569) are left out on purpose, so those ZIPs
 * never fill a state.
 *
 * Each entry is [first prefix, last prefix, USPS state code], inclusive.
 */
export const ZIP_PREFIX_STATE_RANGES: readonly (readonly [string, string, string])[] = [
  ['005', '005', 'NY'],
  ['010', '027', 'MA'],
  ['028', '029', 'RI'],
  ['030', '038', 'NH'],
  ['039', '049', 'ME'],
  ['050', '054', 'VT'],
  ['055', '055', 'MA'],
  ['056', '059', 'VT'],
  ['060', '069', 'CT'],
  ['070', '089', 'NJ'],
  ['100', '149', 'NY'],
  ['150', '196', 'PA'],
  ['197', '199', 'DE'],
  ['200', '200', 'DC'],
  ['201', '201', 'VA'],
  ['202', '205', 'DC'],
  ['206', '212', 'MD'],
  ['214', '219', 'MD'],
  ['220', '246', 'VA'],
  ['247', '268', 'WV'],
  ['270', '289', 'NC'],
  ['290', '299', 'SC'],
  ['300', '319', 'GA'],
  ['320', '339', 'FL'],
  ['341', '342', 'FL'],
  ['344', '344', 'FL'],
  ['346', '347', 'FL'],
  ['349', '349', 'FL'],
  ['350', '352', 'AL'],
  ['354', '369', 'AL'],
  ['370', '385', 'TN'],
  ['386', '397', 'MS'],
  ['398', '399', 'GA'],
  ['400', '418', 'KY'],
  ['420', '427', 'KY'],
  ['430', '459', 'OH'],
  ['460', '479', 'IN'],
  ['480', '499', 'MI'],
  ['500', '516', 'IA'],
  ['520', '528', 'IA'],
  ['530', '532', 'WI'],
  ['534', '535', 'WI'],
  ['537', '549', 'WI'],
  ['550', '551', 'MN'],
  ['553', '567', 'MN'],
  ['570', '577', 'SD'],
  ['580', '588', 'ND'],
  ['590', '599', 'MT'],
  ['600', '620', 'IL'],
  ['622', '629', 'IL'],
  ['630', '631', 'MO'],
  ['633', '641', 'MO'],
  ['644', '658', 'MO'],
  ['660', '662', 'KS'],
  ['664', '679', 'KS'],
  ['680', '681', 'NE'],
  ['683', '693', 'NE'],
  ['700', '701', 'LA'],
  ['703', '708', 'LA'],
  ['710', '714', 'LA'],
  ['716', '729', 'AR'],
  ['730', '731', 'OK'],
  ['733', '733', 'TX'],
  ['734', '741', 'OK'],
  ['743', '749', 'OK'],
  ['750', '770', 'TX'],
  ['772', '799', 'TX'],
  ['800', '816', 'CO'],
  ['820', '831', 'WY'],
  ['832', '838', 'ID'],
  ['840', '847', 'UT'],
  ['850', '853', 'AZ'],
  ['855', '857', 'AZ'],
  ['859', '860', 'AZ'],
  ['863', '865', 'AZ'],
  ['870', '871', 'NM'],
  ['873', '875', 'NM'],
  ['877', '884', 'NM'],
  ['885', '885', 'TX'],
  ['889', '891', 'NV'],
  ['893', '895', 'NV'],
  ['897', '898', 'NV'],
  ['900', '908', 'CA'],
  ['910', '928', 'CA'],
  ['930', '961', 'CA'],
  ['967', '968', 'HI'],
  ['970', '979', 'OR'],
  ['980', '986', 'WA'],
  ['988', '994', 'WA'],
  ['995', '999', 'AK'],
];

/**
 * The USPS state code for a full 5-digit ZIP, or undefined when the ZIP is
 * incomplete or its prefix is not in the table above.
 */
export function stateFromZip(zip: string): string | undefined {
  if (!/^\d{5}$/.test(zip)) return undefined;
  const prefix = zip.slice(0, 3);
  // Prefixes are zero-padded 3-digit strings, so string order is number order.
  return ZIP_PREFIX_STATE_RANGES.find(([first, last]) => prefix >= first && prefix <= last)?.[2];
}
